import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
} from '@mui/material';
import { Search as SearchIcon, Download as DownloadIcon } from '@mui/icons-material';
import { userService, installationService, installationItemService } from '../services/api';
import * as XLSX from 'xlsx';

export default function WorkerReports() {
  const [workers, setWorkers] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [installations, setInstallations] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [workersResponse, itemsResponse] = await Promise.all([
        userService.getUsers({ role: 'worker' }),
        installationItemService.getItems(),
      ]);
      setWorkers(workersResponse.data);
      setAllItems(itemsResponse.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!selectedWorker) {
      setError('Please select a worker');
      return;
    }

    setSearching(true);
    setError('');

    try {
      const filters = { installer: selectedWorker };
      if (dateFrom) filters.from_date = dateFrom;
      if (dateTo) filters.to_date = dateTo;

      const response = await installationService.getInstallations(filters);
      
      if (response.success) {
        setInstallations(response.data);
        calculateTotals(response.data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSearching(false);
    }
  };

  const calculateTotals = (installationsData) => {
    const itemTotals = {};
    
    // Initialize all items to 0
    allItems.forEach((item) => {
      itemTotals[item.name] = 0;
    });

    // Sum up quantities
    installationsData.forEach((inst) => {
      inst.items_used?.forEach((usedItem) => {
        if (itemTotals[usedItem.item_name] !== undefined) {
          itemTotals[usedItem.item_name] += usedItem.quantity;
        }
      });
    });

    setTotals(itemTotals);
  };

  const handleExportToExcel = () => {
    setExporting(true);

    try {
      const worker = workers.find((w) => w._id === selectedWorker);
      
      // Prepare data for Excel
      const excelData = installations.map((inst) => {
        const row = {
          'التاريخ': new Date(inst.installation_date).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }),
          'الوقت': inst.installation_time || '',
          'المدينة': inst.city || '',
          'المقسم': inst.district || '',
          'نوع العداد': getMeterTypeLabel(inst.meter_type),
          'نوع التركيب': getInstallationTypeLabel(inst.installation_type),
          'الاندكس': inst.index || '',
          'الباركود': inst.barcode || '',
          'رقم العداد الجديد': inst.new_meter_number || '',
          'رقم العداد القديم': inst.old_meter_number || '',
          'ملاحظات': inst.notes || '',
        };

        // Add all items as columns
        allItems.forEach((item) => {
          const usedItem = inst.items_used?.find((ui) => ui.item_name === item.name);
          row[item.name] = usedItem ? usedItem.quantity : 0;
        });

        return row;
      });

      // Add totals row
      const totalsRow = {
        'التاريخ': 'الإجمالي',
        'الوقت': '',
        'المدينة': '',
        'المقسم': '',
        'نوع العداد': '',
        'نوع التركيب': '',
        'الاندكس': installations.length,
        'الباركود': '',
        'رقم العداد الجديد': '',
        'رقم العداد القديم': '',
        'ملاحظات': '',
      };

      allItems.forEach((item) => {
        totalsRow[item.name] = totals[item.name] || 0;
      });

      excelData.push(totalsRow);

      // Create workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const colWidths = [
        { wch: 15 }, { wch: 12 }, { wch: 15 }, { wch: 15 },
        { wch: 18 }, { wch: 18 }, { wch: 10 }, { wch: 18 },
        { wch: 18 }, { wch: 18 }, { wch: 30 },
      ];

      allItems.forEach(() => colWidths.push({ wch: 15 }));
      ws['!cols'] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, 'تقرير العامل');

      // Generate file name
      const workerName = worker?.username || 'worker';
      const fileName = `worker_report_${workerName}_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Download file
      XLSX.writeFile(wb, fileName);

      alert('Excel file downloaded successfully!');
    } catch (error) {
      console.error('Export error:', error);
      setError('Failed to export: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  const getMeterTypeLabel = (type) => {
    const labels = {
      residential: 'منزلي',
      commercial: 'تجاري',
      agricultural: 'زراعي',
      industrial: 'صناعي',
    };
    return labels[type] || type;
  };

  const getInstallationTypeLabel = (type) => {
    const labels = {
      buildings: 'عمارات',
      pole: 'بالو',
      wall: 'جداري',
      standalone: 'منسلة',
    };
    return labels[type] || type;
  };

  const selectedWorkerData = workers.find((w) => w._id === selectedWorker);

  return (
    <Box>
      <Typography variant='h4' gutterBottom>
        Worker Reports
      </Typography>

      {error && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Search Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Select Worker</InputLabel>
                <Select
                  value={selectedWorker}
                  label='Select Worker'
                  onChange={(e) => setSelectedWorker(e.target.value)}
                  disabled={loading}
                >
                  {workers.map((worker) => (
                    <MenuItem key={worker._id} value={worker._id}>
                      {worker.full_name || worker.username} (@{worker.username})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type='date'
                label='From Date'
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type='date'
                label='To Date'
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant='contained'
                startIcon={searching ? <CircularProgress size={20} color='inherit' /> : <SearchIcon />}
                onClick={handleSearch}
                disabled={searching || !selectedWorker}
                sx={{ height: 56 }}
              >
                {searching ? 'Searching...' : 'Search'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Worker Summary */}
      {selectedWorkerData && installations.length > 0 && (
        <Card sx={{ mb: 3, bgcolor: 'primary.main', color: 'white' }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Typography variant='body2' sx={{ opacity: 0.9 }}>
                  Worker
                </Typography>
                <Typography variant='h6' fontWeight='bold'>
                  {selectedWorkerData.full_name || selectedWorkerData.username}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant='body2' sx={{ opacity: 0.9 }}>
                  Team
                </Typography>
                <Typography variant='h6' fontWeight='bold'>
                  {selectedWorkerData.team?.name || 'Not assigned'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant='body2' sx={{ opacity: 0.9 }}>
                  Current Credit
                </Typography>
                <Typography variant='h6' fontWeight='bold'>
                  {selectedWorkerData.meter_credit} meters
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant='body2' sx={{ opacity: 0.9 }}>
                  Total Installations
                </Typography>
                <Typography variant='h6' fontWeight='bold'>
                  {installations.length}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Installations Table */}
      {installations.length > 0 && (
        <>
          <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
            <Typography variant='h5'>
              Installations ({installations.length})
            </Typography>
            <Button
              variant='contained'
              color='success'
              startIcon={exporting ? <CircularProgress size={20} color='inherit' /> : <DownloadIcon />}
              onClick={handleExportToExcel}
              disabled={exporting}
            >
              {exporting ? 'Exporting...' : 'Export to Excel'}
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>#</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>City</strong></TableCell>
                  <TableCell><strong>District</strong></TableCell>
                  <TableCell><strong>Meter Type</strong></TableCell>
                  <TableCell><strong>Installation Type</strong></TableCell>
                  <TableCell><strong>Barcode</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {installations.map((inst) => (
                  <TableRow key={inst._id} hover>
                    <TableCell>#{inst.index}</TableCell>
                    <TableCell>{new Date(inst.installation_date).toLocaleDateString()}</TableCell>
                    <TableCell>{inst.city}</TableCell>
                    <TableCell>{inst.district}</TableCell>
                    <TableCell>{getMeterTypeLabel(inst.meter_type)}</TableCell>
                    <TableCell>{getInstallationTypeLabel(inst.installation_type)}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{inst.barcode}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Materials Summary */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant='h5' gutterBottom color='primary'>
                📊 Materials Summary
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                {/* Total Installations */}
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      p: 3,
                      bgcolor: 'primary.main',
                      color: 'white',
                      borderRadius: 2,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant='body2' sx={{ opacity: 0.9 }}>
                      Total Installations
                    </Typography>
                    <Typography variant='h3' fontWeight='bold'>
                      {installations.length}
                    </Typography>
                  </Box>
                </Grid>

                {/* Total Items */}
                <Grid item xs={12} md={8}>
                  <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2 }}>
                    <Typography variant='h6' gutterBottom>
                      Materials Used
                    </Typography>
                    <Grid container spacing={1.5}>
                      {Object.entries(totals)
                        .filter(([_, quantity]) => quantity > 0)
                        .map(([itemName, quantity]) => {
                          const item = allItems.find((i) => i.name === itemName);
                          return (
                            <Grid item xs={12} sm={6} md={4} key={itemName}>
                              <Box
                                sx={{
                                  p: 1.5,
                                  bgcolor: 'white',
                                  borderRadius: 1,
                                  border: '1px solid #e0e0e0',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                }}
                              >
                                <Box>
                                  <Typography variant='body2' fontWeight={600}>
                                    {itemName}
                                  </Typography>
                                  <Typography variant='caption' color='text.secondary'>
                                    {item?.unit || 'piece'}
                                  </Typography>
                                </Box>
                                <Chip
                                  label={quantity}
                                  color='success'
                                  size='medium'
                                  sx={{ fontWeight: 'bold' }}
                                />
                              </Box>
                            </Grid>
                          );
                        })}
                    </Grid>
                    {Object.values(totals).every((v) => v === 0) && (
                      <Alert severity='info' sx={{ mt: 2 }}>
                        No materials were used in these installations
                      </Alert>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Detailed Items Table */}
          <Card>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                Detailed Items by Installation
              </Typography>
              <TableContainer>
                <Table size='small'>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell><strong>Index</strong></TableCell>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>City</strong></TableCell>
                      {allItems
                        .filter((item) => totals[item.name] > 0)
                        .map((item) => (
                          <TableCell key={item._id} align='center'>
                            <strong>{item.name}</strong>
                            <br />
                            <Typography variant='caption' color='text.secondary'>
                              ({item.unit})
                            </Typography>
                          </TableCell>
                        ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {installations.map((inst) => (
                      <TableRow key={inst._id} hover>
                        <TableCell>#{inst.index}</TableCell>
                        <TableCell>
                          {new Date(inst.installation_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{inst.city}</TableCell>
                        {allItems
                          .filter((item) => totals[item.name] > 0)
                          .map((item) => {
                            const usedItem = inst.items_used?.find(
                              (ui) => ui.item_name === item.name
                            );
                            return (
                              <TableCell key={item._id} align='center'>
                                {usedItem ? usedItem.quantity : '-'}
                              </TableCell>
                            );
                          })}
                      </TableRow>
                    ))}
                    {/* Totals Row */}
                    <TableRow sx={{ bgcolor: 'success.light' }}>
                      <TableCell colSpan={3}>
                        <strong>الإجمالي (Total)</strong>
                      </TableCell>
                      {allItems
                        .filter((item) => totals[item.name] > 0)
                        .map((item) => (
                          <TableCell key={item._id} align='center'>
                            <Chip
                              label={totals[item.name]}
                              color='success'
                              size='small'
                              sx={{ fontWeight: 'bold' }}
                            />
                          </TableCell>
                        ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}

      {/* No Results */}
      {!loading && !searching && installations.length === 0 && selectedWorker && (
        <Card>
          <CardContent>
            <Alert severity='info'>
              No installations found for the selected criteria. Try adjusting the date range or select a different worker.
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Initial State */}
      {!loading && !selectedWorker && (
        <Card>
          <CardContent>
            <Box textAlign='center' py={4}>
              <Typography variant='h6' color='text.secondary' gutterBottom>
                Select a worker and date range to view their installation report
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                You can leave the date fields empty to see all installations
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

